import {UserProvider} from '@auth0/nextjs-auth0/client'
import {Provider as JotaiProvider} from 'jotai'
import {NextComponentType} from 'next'
import {AppProps} from 'next/app'
import Head from 'next/head'
import PlausibleProvider from 'next-plausible'

import env from 'lib/config/public-env'
import {FONT_URL} from 'lib/constants'

import ErrorHandler, {
  ApiStatusToaster,
  ErrorToaster
} from 'lib/components/app-error-handler'
import ChakraTheme from 'lib/config/chakra'
import SWRWrapper from 'lib/config/swr'
import {simplifyQueryParams} from 'lib/hooks/use-router-query'
import EmptyLayout from 'lib/layouts/empty'
import WebGlIsSupportedToaster from 'lib/map/components/webgl-is-supported-toaster'

import '../styles.css'

// Components that have a layout
type ComponentWithLayout = NextComponentType & {
  Layout: React.FC<{}>
}

// Two domains allows us to track specific deployments and a combination of all deployments.
const plausibleDomain = [env.PLAUSIBLE_DOMAIN, 'global.conveyal.com'].join(',')

export default function ConveyalAnalysis({
  Component,
  pageProps,
  router
}: AppProps) {
  const Layout = Object.prototype.hasOwnProperty.call(Component, 'Layout')
    ? (Component as ComponentWithLayout).Layout
    : EmptyLayout
  return (
    <PlausibleProvider domain={plausibleDomain}>
      <UserProvider>
        <style jsx global>{`
          @font-face {
            font-family: 'Inter';
            font-style: normal;
            font-weight: 100 900;
            font-display: optional;
            src: url(${FONT_URL}) format('woff2');
            unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6,
              U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191,
              U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
          }
        `}</style>
        <ChakraTheme>
          <ErrorHandler>
            <JotaiProvider>
              <SWRWrapper>
                <ApiStatusToaster />
                <ErrorToaster />
                <WebGlIsSupportedToaster />

                <Head>
                  <title>Conveyal Analysis</title>
                </Head>
                <Layout>
                  <Component
                    query={simplifyQueryParams(router.query)}
                    {...pageProps}
                  />
                </Layout>
              </SWRWrapper>
            </JotaiProvider>
          </ErrorHandler>
        </ChakraTheme>
      </UserProvider>
    </PlausibleProvider>
  )
}
